// StudyDost — Student My Report Page
// frontend/src/pages/MyReportPage.js

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const SUBJECT_COLORS = {
  MATH: '#3B82F6', SCIENCE: '#10B981', PHYSICS: '#8B5CF6',
  CHEMISTRY: '#F59E0B', BIOLOGY: '#EC4899', ENGLISH: '#06B6D4',
  HINDI: '#EF4444', HISTORY: '#F97316', GEOGRAPHY: '#84CC16',
  COMPUTER: '#6366F1', OTHER: '#94A3B8',
};

const LEVEL_ORDER = ['Beginner', 'Explorer', 'Scholar', 'Expert', 'Master'];
const LEVEL_XP    = [0, 100, 300, 700, 1500];

function getLevelProgress(totalXP) {
  let ci = 0;
  for (let i = 0; i < LEVEL_XP.length; i++) { if (totalXP >= LEVEL_XP[i]) ci = i; }
  const next = ci + 1 < LEVEL_ORDER.length;
  const progress = next
    ? Math.round(((totalXP - LEVEL_XP[ci]) / (LEVEL_XP[ci + 1] - LEVEL_XP[ci])) * 100)
    : 100;
  return { current: LEVEL_ORDER[ci], next: next ? LEVEL_ORDER[ci + 1] : null, progress, xpToNext: next ? LEVEL_XP[ci + 1] - totalXP : 0 };
}

export default function MyReportPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['myReport'],
    queryFn: () => api.get('/users/my-report').then(r => r.data),
    retry: false,
    staleTime: 2 * 60 * 1000,
  });

  const maxActivity = data ? Math.max(...data.dailyActivity.map(d => d.count), 1) : 1;
  const lp = data ? getLevelProgress(data.totalXP) : null;

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <button style={s.backBtn} onClick={() => navigate(-1)}><ArrowLeft size={22} color="#1E40AF" /></button>
        <div style={s.headerTitle}>My Report 📊</div>
        <div style={{ width: 36 }} />
      </div>

      {isLoading && <div style={s.center}>Loading...</div>}
      {error && <div style={s.center}>Report load nahi ho saki. Please try again.</div>}

      {data && (
        <div style={s.content}>

          {/* Greeting */}
          <div style={s.greetCard}>
            <div style={s.greetName}>Hello, {data.name || user?.name || 'Student'}! 👋</div>
            <div style={s.greetSub}>Class {data.studentClass} · {data.board} · {data.activePlan} Plan</div>
          </div>

          {/* Level + XP */}
          <div style={s.card}>
            <div style={s.cardTitle}>⭐ Level & XP</div>
            <div style={s.levelRow}>
              <span style={s.levelName}>{lp.current}</span>
              {lp.next && <span style={s.levelNext}>→ {lp.next}</span>}
              <span style={s.xpTotal}>{data.totalXP} XP</span>
            </div>
            <div style={s.progressBg}>
              <div style={{ ...s.progressFill, width: `${lp.progress}%` }} />
            </div>
            {lp.next && <div style={s.xpToNext}>{lp.xpToNext} XP more needed for {lp.next} </div>}
          </div>

          {/* Streak stats */}
          <div style={s.statsRow}>
            <div style={{ ...s.statCard, background: '#FFF7ED' }}>
              <div style={s.statEmoji}>{data.streak >= 7 ? '🔥' : data.streak >= 3 ? '⚡' : '📅'}</div>
              <div style={s.statNum}>{data.streak}</div>
              <div style={s.statLabel}>Day Streak</div>
            </div>
            <div style={{ ...s.statCard, background: '#F0FDF4' }}>
              <div style={s.statEmoji}>🏆</div>
              <div style={s.statNum}>{data.longestStreak}</div>
              <div style={s.statLabel}>Best Streak</div>
            </div>
            <div style={{ ...s.statCard, background: '#EFF6FF' }}>
              <div style={s.statEmoji}>❓</div>
              <div style={s.statNum}>{data.totalQuestions}</div>
              <div style={s.statLabel}>Total Questions</div>
            </div>
            <div style={{ ...s.statCard, background: '#F5F3FF' }}>
              <div style={s.statEmoji}>🎖️</div>
              <div style={s.statNum}>{(data.badges || []).length}</div>
              <div style={s.statLabel}>Badges</div>
            </div>
          </div>

          {/* Weekly activity bar chart */}
          <div style={s.card}>
            <div style={s.cardTitle}>📅 This Week's Activity</div>
            <div style={s.chartRow}>
              {data.dailyActivity.map((day, i) => (
                <div key={i} style={s.chartCol}>
                  <div style={s.barWrap}>
                    <div style={{
                      ...s.bar,
                      height: `${Math.round((day.count / maxActivity) * 100)}%`,
                      background: day.count > 0 ? '#4F46E5' : '#E2E8F0',
                    }} />
                  </div>
                  <div style={s.barCount}>{day.count || ''}</div>
                  <div style={s.barLabel}>{day.label}</div>
                </div>
              ))}
            </div>
            <div style={s.weekTotal}>
              This week: <strong>{data.weeklyQuestionCount} questions</strong>
              {data.mostActiveTime && <span style={{ color: '#94A3B8' }}> · Most active at {data.mostActiveTime} </span>}
            </div>
          </div>

          {/* Subject breakdown */}
          {data.subjectBreakdown.length > 0 && (
            <div style={s.card}>
              <div style={s.cardTitle}>📚 Subject Breakdown (this week)</div>
              {data.subjectBreakdown.map(({ subject, count }) => {
                const pct = Math.round((count / data.weeklyQuestionCount) * 100);
                return (
                  <div key={subject} style={s.subjectRow}>
                    <div style={s.subjectLabel}>{subject}</div>
                    <div style={s.subjectBarBg}>
                      <div style={{ ...s.subjectBarFill, width: `${pct}%`, background: SUBJECT_COLORS[subject] || '#94A3B8' }} />
                    </div>
                    <div style={s.subjectCount}>{count}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Badges */}
          {(data.badges || []).length > 0 && (
            <div style={s.card}>
              <div style={s.cardTitle}>🏅 Badges</div>
              <div style={s.badgeRow}>
                {data.badges.map(b => (
                  <div key={b} style={s.badge}>
                    {b === 'streak_3' ? '⚡ 3-Day' : b === 'streak_7' ? '🔥 7-Day' : b === 'streak_14' ? '💎 14-Day' : b === 'streak_30' ? '👑 30-Day' : b}
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#F8FAFF', fontFamily: "'DM Sans', sans-serif", paddingBottom: 140 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', background: '#FFF', borderBottom: '1px solid #E8EEFF', position: 'sticky', top: 0, zIndex: 10 },
  backBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' },
  headerTitle: { fontSize: 17, fontWeight: 700, color: '#1E40AF' },
  center: { textAlign: 'center', padding: 60, color: '#94A3B8' },
  content: { padding: '16px 16px 0' },
  greetCard: { background: 'linear-gradient(135deg, #1E40AF, #4F46E5)', borderRadius: 16, padding: '20px 20px', marginBottom: 12, color: '#FFF' },
  greetName: { fontSize: 20, fontWeight: 800, marginBottom: 4 },
  greetSub: { fontSize: 13, opacity: 0.8 },
  card: { background: '#FFF', borderRadius: 16, padding: '16px', marginBottom: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
  cardTitle: { fontSize: 13, fontWeight: 700, color: '#64748B', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' },
  levelRow: { display: 'flex', alignItems: 'center', marginBottom: 8 },
  levelName: { fontSize: 20, fontWeight: 800, color: '#4F46E5' },
  levelNext: { fontSize: 13, color: '#94A3B8', marginLeft: 8, flex: 1 },
  xpTotal: { fontSize: 15, fontWeight: 700, color: '#7C3AED' },
  progressBg: { background: '#E8E6FF', borderRadius: 99, height: 8, marginBottom: 6 },
  progressFill: { background: 'linear-gradient(90deg, #4F46E5, #7C3AED)', borderRadius: 99, height: '100%', transition: 'width 0.5s' },
  xpToNext: { fontSize: 11, color: '#94A3B8' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 },
  statCard: { borderRadius: 14, padding: '12px 8px', textAlign: 'center' },
  statEmoji: { fontSize: 20, marginBottom: 4 },
  statNum: { fontSize: 22, fontWeight: 800, color: '#0F172A' },
  statLabel: { fontSize: 10, color: '#64748B', marginTop: 2 },
  chartRow: { display: 'flex', gap: 6, alignItems: 'flex-end', height: 100, marginBottom: 8 },
  chartCol: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' },
  barWrap: { flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end' },
  bar: { width: '100%', borderRadius: '4px 4px 0 0', minHeight: 4, transition: 'height 0.3s' },
  barCount: { fontSize: 10, color: '#64748B', marginTop: 2 },
  barLabel: { fontSize: 10, color: '#94A3B8' },
  weekTotal: { fontSize: 12, color: '#64748B', marginTop: 4 },
  subjectRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  subjectLabel: { fontSize: 12, fontWeight: 600, color: '#374151', width: 80, flexShrink: 0 },
  subjectBarBg: { flex: 1, background: '#F1F5F9', borderRadius: 99, height: 8 },
  subjectBarFill: { height: '100%', borderRadius: 99 },
  subjectCount: { fontSize: 12, fontWeight: 700, color: '#374151', width: 20, textAlign: 'right' },
  badgeRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  badge: { background: '#FFF7ED', border: '1.5px solid #FED7AA', borderRadius: 100, padding: '6px 14px', fontSize: 13, fontWeight: 600, color: '#92400E' },
};
